import React, { useState, useEffect } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const PaymentPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    rollNumber: '',
    batch: '',
    course: '',
    paymentReason: '',
    amount: '',
  });

  const [students, setStudents] = useState([]); // To hold the list of students

  // Fetch students on component mount
  useEffect(() => {
    const fetchStudents = async () => {
      try {
        const response = await fetch('/api/v1/students'); // Replace with your students API endpoint
        const data = await response.json();
        if (response.ok) {
          setStudents(data.students); // Assuming API returns { students: [...] }
        } else {
          toast.error('Failed to fetch student data.');
        }
      } catch (error) {
        console.error('Error fetching students:', error);
        toast.error('An error occurred while fetching student data.');
      }
    };

    fetchStudents();

    // Load Razorpay script
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    document.body.appendChild(script);
    return () => document.body.removeChild(script);
  }, []);

  const handleRollNumberChange = (e) => {
    const selectedRollNumber = e.target.value;
    setFormData((prev) => ({ ...prev, rollNumber: selectedRollNumber }));

    // Fetch data for the selected roll number
    const selectedStudent = students.find(
      (student) => student.rollNumber === selectedRollNumber
    );
    if (selectedStudent) {
      setFormData((prev) => ({
        ...prev,
        name: selectedStudent.name,
        batch: selectedStudent.batch,
        course: selectedStudent.course,
      }));
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.amount || formData.amount <= 0 || !formData.name) {
      toast.error('Fill all required details.');
      return;
    }

    try {
      const response = await fetch('/api/v1/payment/razorpay-create-order', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          amount: formData.amount,
          studentDetails: { ...formData },
          paymentReason: formData.paymentReason,
        }),
      });

      const data = await response.json();
      if (!data.success) {
        toast.error('Failed to create payment order.');
        return;
      }

      const options = {
        key: data.paymentDetails.razorpayKey,
        order_id: data.paymentDetails.razorpayOrderId,
        amount: data.paymentDetails.amount * 100,
        name: formData.name,
        description: formData.paymentReason,

        handler: async (response) => {
          try {
            const res = await fetch('/api/v1/payment/razorpay-verify', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                razorpay: response,
                amount: formData.amount,
                ...formData,
              }),
            });

            const data = await res.json();
            if (res.ok && data.success) {
              toast.success(data.message || 'Payment successful!');
            } else {
              toast.error(data.message || 'Payment verification failed.');
            }
          } catch (error) {
            console.error('Error verifying payment:', error);
            toast.error('An error occurred while verifying the payment.');
          }
        },

        prefill: {
          name: formData.name,
          email: formData.email || 'student@example.com',
          contact: formData.contact || '9876543210',
        },
        theme: {
          color: '#00A859',
        },
      };

      const razorpay = new window.Razorpay(options);
      razorpay.open();
    } catch (error) {
      toast.error('Error processing payment.');
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center p-14">
      <div className="bg-white border-4 border-teal-500 rounded-lg shadow-xl p-6 w-4/5 max-w-7xl">
        <h1 className="text-2xl font-bold text-teal-600 mb-6 text-center">
          Student Payment
        </h1>
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Dropdown for Roll Numbers */}
          <div>
            <label htmlFor="rollNumber" className="block text-gray-700 font-medium mb-2">
              Roll Number
            </label>
            <select
              id="rollNumber"
              name="rollNumber"
              value={formData.rollNumber}
              onChange={handleRollNumberChange}
              required
              className="w-full p-3 border rounded-lg"
            >
              <option value="" disabled>
                Select Roll Number
              </option>
              {students.map((student) => (
                <option key={student.rollNumber} value={student.rollNumber}>
                  {student.rollNumber}
                </option>
              ))}
            </select>
          </div>

          {/* Other Input Fields */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="name" className="block text-gray-700 font-medium mb-2">
                Student Name
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                className="w-full p-3 border rounded-lg"
                readOnly
              />
            </div>
            <div>
              <label htmlFor="batch" className="block text-gray-700 font-medium mb-2">
                Batch
              </label>
              <input
                type="text"
                id="batch"
                name="batch"
                value={formData.batch}
                onChange={handleChange}
                required
                className="w-full p-3 border rounded-lg"
                readOnly
              />
            </div>
          </div>

          <div>
            <label htmlFor="course" className="block text-gray-700 font-medium mb-2">
              Course
            </label>
            <input
              type="text"
              id="course"
              name="course"
              value={formData.course}
              onChange={handleChange}
              required
              className="w-full p-3 border rounded-lg"
              readOnly
            />
          </div>

          <div>
            <label htmlFor="paymentReason" className="block text-gray-700 font-medium mb-2">
              Payment Reason
            </label>
            <select
              id="paymentReason"
              name="paymentReason"
              value={formData.paymentReason}
              onChange={handleChange}
              required
              className="w-full p-3 border rounded-lg"
            >
              <option value="" disabled>
                Select Reason
              </option>
              <option value="Tuition Fees">Tuition Fees</option>
              <option value="Hostel Fees">Hostel Fees</option>
              <option value="Examination Fees">Examination Fees</option>
              <option value="Miscellaneous">Miscellaneous</option>
            </select>
          </div>

          <div>
            <label htmlFor="amount" className="block text-gray-700 font-medium mb-2">
              Amount (₹)
            </label>
            <input
              type="number"
              id="amount"
              name="amount"
              value={formData.amount}
              onChange={handleChange}
              required
              className="w-full p-3 border rounded-lg"
            />
          </div>

          <button
            type="submit"
            className="w-full bg-teal-600 text-white py-3 rounded-lg"
          >
            Proceed to Pay
          </button>
        </form>
        <ToastContainer />
      </div>
    </div>
  );
};

export default PaymentPage;
